/**
 * Progressive Web App Utilities
 *
 * Service Worker lifecycle, push notifications, background sync,
 * install prompt handling, cache management, and connectivity awareness.
 */
import logger from './logger';

// ─── Constants ──────────────────────────────────────────────────

const SW_PATH = '/sw.js';
const UPDATE_CHECK_INTERVAL = 60 * 60 * 1000; // 1 hour
const SW_SCOPE = '/';

// ─── Internal state ─────────────────────────────────────────────

let _registration = null;
let _updateAvailable = false;
let _deferredInstallPrompt = null;
let _connectionListeners = [];

// ─── Service Worker Registration ────────────────────────────────

/**
 * Register and manage the Service Worker lifecycle
 * @param {object} [options]
 * @param {Function} [options.onUpdate] - Called when a new SW is available
 * @param {Function} [options.onSuccess] - Called on first successful install
 * @param {boolean} [options.autoReload] - Auto-reload on update (default: false)
 */
export function registerServiceWorker(options = {}) {
  const { onUpdate, onSuccess, autoReload = false } = options;

  if (!('serviceWorker' in navigator)) {
    logger.warn('Service Workers not supported', { userAgent: navigator.userAgent });
    return;
  }

  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register(SW_PATH, { scope: SW_SCOPE })
      .then((registration) => {
        _registration = registration;
        logger.info('Service Worker registered', { scope: registration.scope });

        // Periodic update checks
        setInterval(() => {
          registration.update().catch(() => {});
        }, UPDATE_CHECK_INTERVAL);

        // Handle new SW installing
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;

          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // New version available
                _updateAvailable = true;
                logger.info('New service worker available');

                // Dispatch custom event for UI notification
                window.dispatchEvent(new CustomEvent('sw-update-available'));

                if (onUpdate) {
                  onUpdate(registration);
                }

                if (autoReload) {
                  newWorker.postMessage({ type: 'SKIP_WAITING' });
                }
              } else {
                // First install
                logger.info('Service Worker installed (first time)');
                if (onSuccess) {
                  onSuccess(registration);
                }
              }
            }
          });
        });
      })
      .catch((error) => {
        logger.error('Service Worker registration failed', { error: error.message });
      });

    // Reload page when new SW takes control
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      logger.info('Service Worker controller changed');
      if (_updateAvailable) {
        window.location.reload();
      }
    });
  });
}

/**
 * Unregister service worker and clear all caches
 * @returns {Promise<boolean>} true if successfully unregistered
 */
export async function unregisterServiceWorker() {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();

    // Clear all caches
    if ('caches' in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
      logger.info('Service Worker caches cleared', { cacheCount: keys.length });
    }

    _registration = null;
    _updateAvailable = false;
    logger.info('Service Worker unregistered');
    return success;
  } catch (error) {
    logger.error('Service Worker unregistration failed', { error: error.message });
    return false;
  }
}

/**
 * Apply pending update (skip waiting, reload)
 */
export function applyUpdate() {
  if (!_registration?.waiting) return;
  _registration.waiting.postMessage({ type: 'SKIP_WAITING' });
}

/**
 * Check if an update is available
 */
export function isUpdateAvailable() {
  return _updateAvailable;
}

/**
 * Get service worker registration
 */
export function getRegistration() {
  return _registration;
}

// ─── Cache Management ───────────────────────────────────────────

/**
 * Get total cache size (approximate, in bytes)
 */
export async function getCacheSize() {
  if (!('caches' in window)) return 0;

  try {
    if ('storage' in navigator && 'estimate' in navigator.storage) {
      const estimate = await navigator.storage.estimate();
      return {
        usage: estimate.usage || 0,
        quota: estimate.quota || 0,
        percentUsed: estimate.quota
          ? Math.round(((estimate.usage || 0) / estimate.quota) * 100)
          : 0,
      };
    }
  } catch (_e) { /* fallback */ }

  return { usage: 0, quota: 0, percentUsed: 0 };
}

/**
 * List all cache names
 */
export async function listCaches() {
  if (!('caches' in window)) return [];
  return caches.keys();
}

/**
 * Clear a specific cache
 */
export async function clearCache(cacheName) {
  if (!('caches' in window)) return false;
  return caches.delete(cacheName);
}

/**
 * Clear all caches
 */
export async function clearAllCaches() {
  if (!('caches' in window)) return;
  const keys = await caches.keys();
  await Promise.all(keys.map((k) => caches.delete(k)));
  logger.info('All caches cleared', { count: keys.length });
}

// ─── Notifications ──────────────────────────────────────────────

/**
 * Request notification permission
 * @returns {Promise<boolean>} true if granted
 */
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    logger.warn('Browser does not support notifications');
    return false;
  }

  if (Notification.permission === 'granted') {
    return true;
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }

  return false;
}

/**
 * Get current notification permission status
 * @returns {'granted'|'denied'|'default'|'unsupported'}
 */
export function getNotificationStatus() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

/**
 * Show a notification (via Service Worker for persistence)
 * @param {string} title
 * @param {object} [options] - NotificationOptions + { onClick, onClose }
 */
export async function showNotification(title, options = {}) {
  const { onClick, onClose, ...notifOptions } = options;

  const hasPermission = await requestNotificationPermission();
  if (!hasPermission) return;

  const defaults = {
    icon: '/favicon.svg',
    badge: '/favicon.svg',
    vibrate: [200, 100, 200],
    tag: `gabhan-${Date.now()}`,
    renotify: false,
    requireInteraction: false,
  };

  // Prefer SW notification (survives tab close)
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.ready;
      await registration.showNotification(title, { ...defaults, ...notifOptions });
      return;
    } catch (_e) { /* fallback to Notification API */ }
  }

  // Fallback: standard Notification
  const notification = new Notification(title, { ...defaults, ...notifOptions });
  if (onClick) notification.onclick = onClick;
  if (onClose) notification.onclose = onClose;
}

// ─── Background Sync ────────────────────────────────────────────

/**
 * Register a background sync tag
 * @param {string} tag - Sync identifier
 * @returns {Promise<boolean>}
 */
export async function registerSync(tag) {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('sync' in registration) {
      await registration.sync.register(tag);
      logger.info('Background sync registered', { tag });
      return true;
    }
  } catch (error) {
    logger.error('Background sync registration failed', { error: error.message, tag });
  }
  return false;
}

/**
 * Register a periodic background sync (if supported)
 * @param {string} tag
 * @param {number} minInterval - Minimum interval in ms
 */
export async function registerPeriodicSync(tag, minInterval = 12 * 60 * 60 * 1000) {
  if (!('serviceWorker' in navigator)) return false;

  try {
    const registration = await navigator.serviceWorker.ready;
    if ('periodicSync' in registration) {
      const status = await navigator.permissions.query({ name: 'periodic-background-sync' });
      if (status.state === 'granted') {
        await registration.periodicSync.register(tag, { minInterval });
        logger.info('Periodic sync registered', { tag, minInterval });
        return true;
      }
    }
  } catch (error) {
    logger.warn('Periodic sync not available', { error: error.message });
  }
  return false;
}

// ─── Install Prompt ─────────────────────────────────────────────

/**
 * Listen for beforeinstallprompt to enable custom install UI
 */
export function initInstallPrompt() {
  if (typeof window === 'undefined') return;

  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    _deferredInstallPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
    logger.info('Install prompt captured');
  });

  window.addEventListener('appinstalled', () => {
    _deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
    logger.info('App installed');
  });
}

/**
 * Show the install prompt (call from user gesture handler)
 * @returns {Promise<'accepted'|'dismissed'|'unavailable'>}
 */
export async function showInstallPrompt() {
  if (!_deferredInstallPrompt) return 'unavailable';

  _deferredInstallPrompt.prompt();
  const result = await _deferredInstallPrompt.userChoice;
  _deferredInstallPrompt = null;
  return result.outcome; // 'accepted' or 'dismissed'
}

/**
 * Check if install prompt is available
 */
export function canInstall() {
  return !!_deferredInstallPrompt;
}

// ─── Standalone detection ───────────────────────────────────────

/**
 * Check if app is running in standalone (installed) mode
 */
export function isStandalone() {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    window.navigator.standalone === true ||
    document.referrer.includes('android-app://')
  );
}

// ─── Connectivity ───────────────────────────────────────────────

/**
 * Check online status
 */
export function isOnline() {
  return navigator.onLine;
}

/**
 * Get connection quality info
 * @returns {{ online: boolean, type: string, downlink: number, saveData: boolean }}
 */
export function getConnectionInfo() {
  const conn = navigator.connection || {};
  return {
    online: navigator.onLine,
    type: conn.effectiveType || 'unknown',
    downlink: conn.downlink || 0,
    rtt: conn.rtt || 0,
    saveData: conn.saveData || false,
  };
}

/**
 * Listen for online/offline events
 * @param {Function} onOnline
 * @param {Function} onOffline
 * @returns {Function} cleanup function
 */
export function addConnectionListeners(onOnline, onOffline) {
  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);

  // Track for cleanup
  _connectionListeners.push({ onOnline, onOffline });

  return () => {
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    _connectionListeners = _connectionListeners.filter(
      (l) => l.onOnline !== onOnline
    );
  };
}

// ─── Diagnostics ────────────────────────────────────────────────

/**
 * Get PWA diagnostic info
 */
export async function getPWADiagnostics() {
  const cacheInfo = await getCacheSize();
  return {
    swSupported: 'serviceWorker' in navigator,
    swRegistered: !!_registration,
    swState: _registration?.active?.state || null,
    updateAvailable: _updateAvailable,
    standalone: isStandalone(),
    canInstall: canInstall(),
    notificationPermission: getNotificationStatus(),
    connection: getConnectionInfo(),
    cache: cacheInfo,
    backgroundSyncSupported: 'SyncManager' in window,
    pushSupported: 'PushManager' in window,
  };
}
